import { useEffect, useState } from "react";
import {
  Check,
  Headphones,
  MessageCircleMore,
  MessageSquareText,
  MonitorUp,
  UsersRound,
  Video,
} from "lucide-react";
import { productHref } from "../data/products";

const TOP_ANGLE = -90;
const SHOW_MS = 3200;
const MOVE_MS = 1800;

function shortestOrbitDelta(fromAngle, toAngle) {
  return ((toAngle - fromAngle + 540) % 360) - 180;
}

const realAvatars = [
  "/avatars/avatar-1.png",
  "/avatars/avatar-2.png",
  "/avatars/avatar-3.png",
  "/avatars/avatar-4.png",
];

const orbitCards = [
  {
    key: "chat",
    focusType: "chat",
    hrefSlug: "messaging",
    title: "Chat",
    lines: ["Real-time conversations", "with your team"],
    cta: "Open Chat",
    Icon: MessageCircleMore,
    accent: "purple",
    angle: 90,
    width: "202px",
    height: "160px",
    avatars: realAvatars,
    extraCount: "+8",
    alt:" "
  },
  {
    key: "video-call",
    focusType: "video",
    hrefSlug: "calls",
    title: "Video Call",
    lines: ["Start or join HD video", "meetings in one click"],
    cta: "Join Meeting",
    Icon: Video,
    accent: "blue",
    angle: 150,
    width: "236px",
    height: "160px",
    avatars: realAvatars,
    alt:" "
  },
  {
    key: "tasks",
    focusType: "tasks",
    hrefSlug: "ticketing",
    title: "Ticketing",
    lines: ["Assign, track and complete", "work effortlessly"],
    cta: "View Tasks",
    Icon: Check,
    accent: "green",
    angle: 210,
    width: "232px",
    height: "160px",
    progress: 75,
  },
  {
    key: "support",
    focusType: "support",
    hrefSlug: "ticketing",
    title: "Support",
    lines: ["Resolve issues faster with", "smart support"],
    cta: "View Tickets",
    Icon: Headphones,
    accent: "purple",
    angle: -90,
    width: "214px",
    height: "160px",
    avatars: realAvatars,
    extraCount: "+8",
  },
  {
    key: "team",
    focusType: "team",
    hrefSlug: "home",
    title: "Team",
    lines: ["Manage teams, roles and", "collaboration"],
    cta: "View Team",
    Icon: UsersRound,
    accent: "purple",
    angle: -30,
    width: "214px",
    height: "160px",
    avatars: realAvatars,
    extraCount: "+16",
  },
  {
    key: "messaging",
    focusType: "messaging",
    hrefSlug: "messaging",
    title: "Messaging",
    lines: ["Keep everyone in the loop", "instantly"],
    cta: "View Messages",
    Icon: MessageSquareText,
    accent: "green",
    angle: 30,
    width: "214px",
    height: "160px",
    avatars: realAvatars,
    extraCount: "+12",
  },
];

const electronDots = [
  { ringClass: "loop-electron--outer-a", radius: "clamp(222px, 25vw, 302px)", color: "var(--electron-purple)", duration: "13.5s", delay: "-1.4s" },
  { ringClass: "loop-electron--outer-b", radius: "clamp(222px, 25vw, 302px)", color: "var(--electron-blue)", duration: "16.5s", delay: "-7.8s" },
  { ringClass: "loop-electron--middle", radius: "clamp(186px, 21vw, 250px)", color: "var(--electron-green)", duration: "11.8s", delay: "-4.3s" },
  { ringClass: "loop-electron--inner", radius: "clamp(150px, 17vw, 198px)", color: "var(--electron-purple)", duration: "10.4s", delay: "-2.8s" },
  { ringClass: "loop-electron--small-a", radius: "clamp(112px, 13vw, 152px)", color: "var(--electron-blue)", duration: "8.8s", delay: "-1.2s" },
  { ringClass: "loop-electron--small-b", radius: "clamp(76px, 9vw, 108px)", color: "var(--electron-green)", duration: "7.4s", delay: "-3.6s" },
];

function Avatar() {
  return (
    <span className="loop-focus-avatar">
      <img src="/avatars/avatar-1.png" alt="" />
    </span>
  );
}

function MessageBubble({ side = "left", name, text, className = "" }) {
  return (
    <div className={`loop-msg-row loop-msg-row--${side} ${className}`}>
      {side === "left" ? <Avatar /> : null}
      <div className={`loop-msg-bubble ${side === "right" ? "loop-msg-bubble--right" : ""}`}>
        <strong>{name}</strong>
        <small>{text}</small>
      </div>
      {side === "right" ? <Avatar /> : null}
    </div>
  );
}

function FocusExperience({ card }) {
  if (card.focusType === "video") {
    return (
      <>
        <div className="loop-mini-call-grid">
          <div className="loop-mini-call-tile loop-mini-call-tile--main">
            <img src="/avatars/avatar-1.png" alt="" />
            <span>Maya</span>
          </div>
          <div className="loop-mini-call-tile">
            <img src="/avatars/avatar-2.png" alt="" />
            <span>Arjun</span>
          </div>
        </div>
        <div className="loop-focus-event loop-focus-event--one"><Video size={14} /> HD call connected</div>
        <div className="loop-focus-event loop-focus-event--two"><MonitorUp size={14} /> Screen sharing started</div>
      </>
    );
  }

  if (card.focusType === "tasks") {
    return (
      <div className="loop-live-task-list">
        <div className="loop-live-task loop-live-task--one"><span><Check size={12} /></span><div><strong>Homepage review</strong><small>Completed just now</small></div><em>Done</em></div>
        <div className="loop-live-task loop-live-task--two"><span>2</span><div><strong>Client follow-up</strong><small>Assigned to Maya</small></div><em>Live</em></div>
        <div className="loop-live-task loop-live-task--three"><span>3</span><div><strong>Launch checklist</strong><small>Progress updated</small></div><em>82%</em></div>
        <div className="loop-live-progress"><i /></div>
      </div>
    );
  }

  if (card.focusType === "support") {
    return (
      <div className="loop-ticket-stream">
        <div className="loop-ticket-card loop-ticket-card--one"><b>TK-2048</b><span>Client access issue</span><em>HIGH</em></div>
        <div className="loop-ticket-flow loop-ticket-flow--two"><Avatar /><div><strong>Assigned to Support</strong><small>Owner updated live</small></div></div>
        <div className="loop-ticket-flow loop-ticket-flow--three"><span className="loop-ticket-check"><Check size={13} /></span><div><strong>Issue resolved</strong><small>Status moved to Closed</small></div></div>
      </div>
    );
  }

  if (card.focusType === "team") {
    return (
      <div className="loop-team-live-grid">
        {[
          ["Maya", "Design"],
          ["Arjun", "Support"],
          ["Nina", "Operations"],
          ["Jai", "Product"],
        ].map(([name, role], index) => (
          <div className={`loop-team-live-person loop-team-live-person--${index + 1}`} key={name}>
            <Avatar />
            <div><strong>{name}</strong><small>{role}</small></div>
            <i />
          </div>
        ))}
        <div className="loop-team-presence">4 teammates active in this workspace</div>
      </div>
    );
  }

  if (card.focusType === "chat") {
    return (
      <div className="loop-focus-stream">
        <MessageBubble side="left" name="Maya" text="Can we review the latest build?" className="loop-msg-row--one" />
        <MessageBubble side="right" name="You" text="Yes — opening it now." className="loop-msg-row--two" />
        <MessageBubble side="left" name="Maya" text="Perfect. I’m in the workspace." className="loop-msg-row--three" />
      </div>
    );
  }

  return (
    <div className="loop-focus-stream">
      <MessageBubble side="left" name="Maya" text="New client message came in." className="loop-msg-row--one" />
      <MessageBubble side="right" name="Cullath Desk" text="Reply sent from the support side." className="loop-msg-row--two" />
      <MessageBubble side="left" name="Live team chat" text="Typing… message synced instantly." className="loop-msg-row--three" />
    </div>
  );
}

export default function OrbitHero() {
  const [focusIndex, setFocusIndex] = useState(5); // Start with Messaging at the top.
  const [phase, setPhase] = useState("show"); // show popup first, then move to the next card.
  const focusCard = orbitCards[focusIndex];
  const orbitPauseDelta = shortestOrbitDelta(focusCard.angle, TOP_ANGLE);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (phase === "show") {
        setPhase("move");
        setFocusIndex((current) => (current + 1) % orbitCards.length);
      } else {
        setPhase("show");
      }
    }, phase === "show" ? SHOW_MS : MOVE_MS);

    return () => clearTimeout(timer);
  }, [phase, focusIndex]);

  return (
    <section className={`cullath-loop-stage ${phase === "show" ? "is-phase-show" : "is-phase-move"}`} aria-label="Animated Cullath workspace orbit">
      <div className="loop-nebula loop-nebula--one" aria-hidden="true" />
      <div className="loop-nebula loop-nebula--two" aria-hidden="true" />

      <div className="loop-orbit-lines" aria-hidden="true">
        <span className="loop-ring loop-ring--outer" />
        <span className="loop-ring loop-ring--middle" />
        <span className="loop-ring loop-ring--inner" />
        <span className="loop-ring loop-ring--tilt-a" />
        <span className="loop-ring loop-ring--tilt-b" />

        <div className="loop-electron-layer">
          {electronDots.map((dot) => (
            <span
              key={dot.ringClass}
              className={`loop-electron ${dot.ringClass}`}
              style={{
                "--dot-radius": dot.radius,
                "--dot-color": dot.color,
                "--dot-duration": dot.duration,
                "--dot-delay": dot.delay,
              }}
            />
          ))}
        </div>
      </div>

      <div className="loop-center-core">
        <span className="loop-core-pulse" aria-hidden="true" />
        <span className="loop-core-glow" aria-hidden="true" />
        <div className="loop-core-card">
          <img src="/2.png" alt="Cullath" />
          <strong>Cullath</strong>
          <small>Where Work Orbits.</small>
        </div>
      </div>

      <div className="loop-focus-overlay" aria-hidden="true" key={`focus-${focusCard.key}-${focusIndex}-${phase}`}>
        <div className={`loop-focus-card loop-focus-card--${focusCard.focusType} loop-focus-card--${phase}`}>
          <div className="loop-focus-header">
            <span className={`loop-focus-live-pill loop-focus-live-pill--${focusCard.accent}`}>
              <i /> Live {focusCard.title}
            </span>
            <span className="loop-focus-count">Now active</span>
          </div>

          <FocusExperience card={focusCard} />

          <div className="loop-focus-footer">
            <span className="loop-focus-status-dot" />
            <span>{focusCard.title} moving live inside Cullath</span>
          </div>
        </div>
      </div>

      <div className="loop-card-layer">
        {orbitCards.map((card) => {
          const Icon = card.Icon;
          const isFocusCard = card.key === focusCard.key;
          const currentAngle = card.angle + orbitPauseDelta;

          return (
            <a
              key={card.key}
              className={`loop-work-card loop-work-card--${card.accent} loop-work-card--${card.key} ${isFocusCard ? "is-focus-card" : "is-background-card"}`}
              href={card.key === "team" ? "/" : productHref(card.hrefSlug)}
              style={{
                "--current-angle": `${currentAngle}deg`,
                "--card-w": card.width,
                "--card-h": card.height,
              }}
            >
              <div className="loop-card-top">
                <span className="loop-card-icon"><Icon size={18} strokeWidth={2.4} /></span>
                <div className="loop-card-title-wrap">
                  <strong className="loop-card-title">{card.title}</strong>
                  <div className="loop-card-desc">
                    {card.lines.map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                  </div>
                </div>
              </div>

              {typeof card.progress === "number" ? (
                <div className="loop-card-progress-block">
                  <div className="loop-card-progress-head">
                    <span>Progress</span>
                    <strong>{card.progress}%</strong>
                  </div>
                  <div className="loop-card-progress-bar">
                    <span style={{ width: `${card.progress}%` }} />
                  </div>
                </div>
              ) : (
                <div className="loop-card-people-row">
                  <div className="loop-avatar-stack" aria-hidden="true">
                    {card.avatars.map((avatarSrc, index) => (
                      <span key={avatarSrc + index} className="loop-avatar-photo-wrap">
                        <img className="loop-avatar-photo" src={avatarSrc} alt="" />
                      </span>
                    ))}
                  </div>
                  {card.extraCount ? <span className="loop-avatar-more">{card.extraCount}</span> : null}
                </div>
              )}

              <span className={`loop-card-cta loop-card-cta--${card.accent}`}>
                {card.cta} <span aria-hidden="true">→</span>
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
